export interface TopicCategoryMenuOptions {
	topicId: string;
	anchorElement: HTMLElement;
	categories: readonly string[];
}

interface ActiveTopicCategoryMenu {
	topicId: string;
	anchorElement: HTMLElement;
	popover: HTMLElement;
	categories: readonly string[];
	rafId: number | null;
	dispose: () => void;
}

let activeMenu: ActiveTopicCategoryMenu | null = null;
const triggerDisposers = new WeakMap<HTMLElement, () => void>();

export function toggleTopicCategoryMenu(options: TopicCategoryMenuOptions): void {
	if (activeMenu?.topicId === options.topicId && activeMenu.anchorElement === options.anchorElement) {
		closeTopicCategoryMenu();
		return;
	}

	closeTopicCategoryMenu();
	openTopicCategoryMenu(options);
}

export function closeTopicCategoryMenu(): void {
	const menu = activeMenu;
	if (!menu) return;

	activeMenu = null;
	menu.dispose();
	menu.popover.remove();
	menu.anchorElement.setAttribute('aria-expanded', 'false');
}

/**
 * Makes a topic-card control a category menu without leaking menu lifecycle
 * concerns into the card's delegated click handler. Returns an unbind function
 * for callers that replace the card subtree.
 */
export function bindTopicCategoryMenuTrigger(options: TopicCategoryMenuOptions): () => void {
	triggerDisposers.get(options.anchorElement)?.();
	options.anchorElement.setAttribute('aria-controls', menuId(options.topicId));
	options.anchorElement.setAttribute('aria-expanded', 'false');

	const onClick = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		toggleTopicCategoryMenu(options);
	};
	const dispose = () => {
		options.anchorElement.removeEventListener('click', onClick);
		triggerDisposers.delete(options.anchorElement);
	};

	options.anchorElement.addEventListener('click', onClick);
	triggerDisposers.set(options.anchorElement, dispose);
	return dispose;
}

function openTopicCategoryMenu(options: TopicCategoryMenuOptions): void {
	const { anchorElement, categories, topicId } = options;
	if (!anchorElement.isConnected) return;

	const popover = document.createElement('div');
	popover.className = 'topic-category-popover';
	popover.style.position = 'fixed';
	popover.id = menuId(topicId);
	popover.dataset.topicCategoryMenu = '';
	popover.dataset.topicId = topicId;

	const list = document.createElement('ul');
	list.className = 'topic-category-popover-list';
	for (const category of categories) {
		const item = document.createElement('li');
		item.textContent = category;
		list.appendChild(item);
	}
	popover.appendChild(list);
	document.body.appendChild(popover);

	anchorElement.setAttribute('aria-controls', popover.id);
	anchorElement.setAttribute('aria-expanded', 'true');

	let rafId: number | null = null;
	const schedulePosition = () => {
		if (rafId !== null) return;
		const position = () => {
			rafId = null;
			positionMenu();
		};
		if (typeof globalThis.requestAnimationFrame === 'function') {
			rafId = globalThis.requestAnimationFrame(position);
		} else {
			position();
		}
	};

	const positionMenu = () => {
		if (activeMenu?.popover !== popover) return;
		if (!anchorElement.isConnected || !popover.isConnected) {
			closeTopicCategoryMenu();
			return;
		}

		const rect = anchorElement.getBoundingClientRect();
		if (!isPositionable(rect)) {
			closeTopicCategoryMenu();
			return;
		}

		const visualViewport = window.visualViewport;
		const viewportWidth = visualViewport?.width || window.innerWidth || document.documentElement.clientWidth;
		const viewportHeight = visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
		if (!viewportWidth || !viewportHeight) {
			closeTopicCategoryMenu();
			return;
		}

		const inset = 8;
		const gap = 8;
		// DOMRect and VisualViewport offsets are both layout-CSS-pixel coordinates.
		// Fixed `top`/`left` values therefore stay in that coordinate space while the
		// visible bounds move with a panned or keyboard-reduced visual viewport.
		const viewportLeft = visualViewport?.offsetLeft ?? 0;
		const viewportTop = visualViewport?.offsetTop ?? 0;
		const viewportRight = viewportLeft + viewportWidth;
		const viewportBottom = viewportTop + viewportHeight;
		const availableHeight = Math.max(0, viewportHeight - inset * 2);
		popover.style.maxHeight = `${availableHeight}px`;
		popover.style.overflowY = 'auto';
		const menuWidth = popover.offsetWidth || Math.min(260, Math.max(0, viewportWidth - inset * 2));
		const naturalMenuHeight = popover.offsetHeight || Math.max(44, categories.length * 28 + 16);
		const menuHeight = Math.min(naturalMenuHeight, availableHeight);
		const left = clamp(rect.left, viewportLeft + inset, Math.max(viewportLeft + inset, viewportRight - menuWidth - inset));
		const spaceBelow = viewportBottom - rect.bottom - inset;
		const spaceAbove = rect.top - viewportTop - inset;
		const shouldOpenDown = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;
		const top = shouldOpenDown
			? clamp(rect.bottom + gap, viewportTop + inset, Math.max(viewportTop + inset, viewportBottom - menuHeight - inset))
			: clamp(rect.top - menuHeight - gap, viewportTop + inset, Math.max(viewportTop + inset, viewportBottom - menuHeight - inset));

		popover.style.left = `${left}px`;
		popover.style.top = `${top}px`;
	};

	const onDocumentClick = (event: MouseEvent) => {
		const target = event.target;
		if (target instanceof Node && (popover.contains(target) || anchorElement.contains(target))) return;
		closeTopicCategoryMenu();
	};
	const onKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') closeTopicCategoryMenu();
	};
	const onScroll = (event: Event) => {
		const target = event.target;
		if (target instanceof Node && popover.contains(target)) {
			schedulePosition();
			return;
		}
		closeTopicCategoryMenu();
	};
	const onAnchorMutation = () => {
		if (!anchorElement.isConnected) closeTopicCategoryMenu();
	};

	document.addEventListener('click', onDocumentClick);
	document.addEventListener('keydown', onKeydown);
	document.addEventListener('scroll', onScroll, true);
	window.addEventListener('resize', schedulePosition);
	window.visualViewport?.addEventListener('resize', schedulePosition);
	window.visualViewport?.addEventListener('scroll', closeTopicCategoryMenu);

	const resizeObserver = typeof globalThis.ResizeObserver === 'function'
		? new globalThis.ResizeObserver(schedulePosition)
		: null;
	resizeObserver?.observe(anchorElement);

	const mutationObserver = typeof globalThis.MutationObserver === 'function'
		? new globalThis.MutationObserver(onAnchorMutation)
		: null;
	mutationObserver?.observe(document.body, { childList: true, subtree: true });

	activeMenu = {
		topicId,
		anchorElement,
		popover,
		categories,
		rafId,
		dispose: () => {
			if (rafId !== null && typeof globalThis.cancelAnimationFrame === 'function') globalThis.cancelAnimationFrame(rafId);
			document.removeEventListener('click', onDocumentClick);
			document.removeEventListener('keydown', onKeydown);
			document.removeEventListener('scroll', onScroll, true);
			window.removeEventListener('resize', schedulePosition);
			window.visualViewport?.removeEventListener('resize', schedulePosition);
			window.visualViewport?.removeEventListener('scroll', closeTopicCategoryMenu);
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
		},
	};
	positionMenu();
}

function menuId(topicId: string): string {
	return `topic-category-menu-${topicId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function isPositionable(rect: DOMRect): boolean {
	return [rect.top, rect.right, rect.bottom, rect.left, rect.width, rect.height].every(Number.isFinite)
		&& rect.right >= rect.left
		&& rect.bottom >= rect.top;
}

function clamp(value: number, lower: number, upper: number): number {
	return Math.max(lower, Math.min(value, upper));
}
